import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group block relative overflow-hidden rounded-lg cursor-pointer"
      >
        <div className="relative w-full h-[420px] md:h-[480px]">
          <img
            src={article.imageUrl}
            alt={article.imageAlt}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/10"></div>
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
            <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-accent-500 text-background-50 mb-3 whitespace-nowrap">
              {article.category}
            </span>
            <h3 className="font-heading text-xl md:text-2xl font-semibold text-white mb-2 leading-snug group-hover:text-accent-300 transition-colors">
              {article.title}
            </h3>
            <p className="text-white/70 text-sm line-clamp-2 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex items-center gap-4 mt-3 text-white/50 text-xs">
              <span>{article.author}</span>
              <span>{article.date}</span>
              <span>{article.readTime}</span>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-4 cursor-pointer p-3 rounded-lg hover:bg-background-100 transition-colors"
      >
        <div className="w-24 h-24 flex-shrink-0 rounded-md overflow-hidden">
          <img
            src={article.imageUrl}
            alt={article.imageAlt}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="font-heading text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
            {article.title}
          </h4>
          <p className="text-xs text-foreground-500 mt-1 line-clamp-2">{article.excerpt}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-foreground-400">
            <span>{article.date}</span>
            <span>{article.readTime}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/70 cursor-pointer hover:border-primary-200 transition-all duration-300"
    >
      <div className="relative w-full h-52 overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.imageAlt}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        <span className="absolute top-3 left-3 px-2.5 py-1 text-xs font-medium rounded-full bg-background-50/90 text-foreground-700 whitespace-nowrap">
          {article.category}
        </span>
      </div>
      <div className="p-5">
        <h3 className="font-heading text-base font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
          {article.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-500 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-background-200/70">
          <div className="flex items-center gap-3 text-xs text-foreground-400">
            <span>{article.author}</span>
            <span>{article.date}</span>
          </div>
          <span className="text-xs text-foreground-400">{article.readTime}</span>
        </div>
      </div>
    </Link>
  );
}